import {Component, effect, inject, OnDestroy, OnInit} from "@angular/core";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {StorageService} from "@shared/services/storage.service";
import {InitService} from "@shared/services/init.service";
import {Router} from "@angular/router";
import {AuthService} from "@shared/services/auth.service";
import {IResendOtp} from "@shared/interfaces/signup.interface";
import {ConfirmPasswordValidator} from "@shared/validators/confirm-password.validator";
import {ButtonComponent} from "@core/components/button/button.component";
import {InputComponent} from "@core/components/input/input.component";
import {IForgotPass} from "@shared/interfaces/forgot.interface";
import {PhoneNumberComponent} from "@core/components/phone-number/phone-number.component";
import {DialogRef} from "@core/shared/utils/dialog/dialog-ref";
import {OnlyNumberDirective} from "@shared/directives/only-number.directive";
import {LoadingService} from "@shared/services/loading.service";

@Component({
  standalone: true,
  selector: "pwj-forgot-password-dialog",
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    ButtonComponent,
    InputComponent,
    PhoneNumberComponent,
    OnlyNumberDirective,
  ],
  templateUrl: "./forgot-password-dialog.component.html",
  styleUrl: "./forgot-password-dialog.component.scss",
})
export class ForgotPasswordDialogComponent implements OnInit, OnDestroy {
  forgotForm: FormGroup;
  forgotOtpForm: FormGroup;
  otpTimer = 120;
  showResendButton = true;
  isVerifyOtpBtnDisable = false;

  private _timerInterval: any;
  private _userInfo!: IResendOtp;

  private readonly _dialogRef = inject(DialogRef);
  private readonly _translateService = inject(TranslateService);
  private readonly _fb = inject(FormBuilder);
  private readonly _storageService = inject(StorageService);
  private readonly _initService = inject(InitService);
  private readonly _loadingService = inject(LoadingService);
  private readonly _router = inject(Router);
  public readonly authService = inject(AuthService);

  constructor() {
    this.forgotForm = this._fb.group({
      mobile: ["", Validators.required],
    });
    this.forgotOtpForm = this._fb.group({
      password: ["", [Validators.required, Validators.minLength(8)]],
      password_confirmation: ["", Validators.required],
      otp: ["", Validators.required],
    }, {validators: ConfirmPasswordValidator});

    effect(() => {
      const userPhone = this.authService.userForgotPassState().mobile;

      if (userPhone) {
        this._userInfo = {
          ...userPhone,
        };
      }
    });
  }

  ngOnInit() {
    this.startOtpTimer();
  }

  public close() {
    this._dialogRef.close();
  }

  startOtpTimer() {
    this.otpTimer = 120;
    this.showResendButton = false;

    this._timerInterval = setInterval(() => {
      if (this.otpTimer > 0) {
        this.otpTimer--;
      } else {
        this.showResendButton = true;
        clearInterval(this._timerInterval);
      }
    }, 1000);
  }

  get formattedTimer() {
    const minutes = Math.floor(this.otpTimer / 60);
    const seconds = this.otpTimer % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }

  submit() {
    this.forgotForm.markAllAsTouched();
    if (this.forgotForm.invalid || !this.forgotForm.dirty) return;
    this._loadingService.setLoading(true);
    let params = this.forgotForm.value;
    this.authService.requestForgotPassword({...params.mobile}).subscribe({
      next: (res) => {
        if (res) {
          this.authService.updateUserForgotPassState({
            state: 1,
            mobile: params.mobile,
            hash: res.hash,
          });
          this._loadingService.setLoading(false);
        }
      },
      error: () => this._loadingService.setLoading(false),
      complete: () => this._loadingService.setLoading(false),
    });
  }

  submitOtp() {
    this.forgotOtpForm.markAllAsTouched();
    if (this.forgotOtpForm.invalid || !this.forgotOtpForm.dirty) return;
    this._loadingService.setLoading(true);
    const params: IForgotPass = {
      ...this.forgotOtpForm.value,
      otp: Number(this.forgotOtpForm.controls["otp"].value),
      hash: this.authService.userForgotPassState().hash,
      mobile: this.authService.userForgotPassState().mobile,
    };

    this.isVerifyOtpBtnDisable = true;

    this.authService.forgotPassword(params).subscribe({
      next: (res) => {
        if (res && res.success) {
          this._storageService.setToken(res.access_token);
          this._storageService.setRefreshToken(res.refresh_token);
          this._initService.init();
          this._loadingService.setLoading(false);
          this.close();
        }
      },
      error: (err) => {
        this.isVerifyOtpBtnDisable = false;
        this._loadingService.setLoading(false);
      },
      complete: ()=> this._loadingService.setLoading(false)
    });

  }

  resendOtp() {
    if (this._userInfo) {
      this.startOtpTimer();

      this.authService.resendOtp({...this._userInfo}).subscribe({
        next: res => {
          this.authService.updateUserForgotPassState({
            hash: res.hash,
          });
        },
        error: error => {
        },
      });
    }
  }

  ngOnDestroy() {
    if (this._timerInterval) {
      clearInterval(this._timerInterval);
    }
  }
}
